import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import { Stack } from '@servicetitan/design-system';
import { Table, TableColumn } from '@servicetitan/table';
import { useInvariableEffect } from '@servicetitan/react-hooks';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Text } from '@servicetitan/anvil2';

import { ActionsCell } from './cells/actions-cell';
import { LoadStatus } from '../../../common/enums/load-status';
import { PartitionsStore } from '../../stores/partitions.store';
import { DebouncedSearch } from '../debounced-search/debounced-search';
import { NotificationsStore } from '../../../common/stores/notifications.store';
import { partitionsQueryParamsHandler } from '../../utils/partitions-query-params-handler';

export const Partitions = provide({
    singletons: [PartitionsStore],
})(
    observer(() => {
        const [
            {
                init,
                tableState,
                queryParams,
                searchFilter,
                filterBySearch,
                deleteLoadStatus,
                partitionsLoadStatus,
            },
            { notifyInSnackbar },
        ] = useDependencies(PartitionsStore, NotificationsStore);

        useInvariableEffect(() => {
            const initialQueryParams = partitionsQueryParamsHandler.parse(window.location.search);

            init(initialQueryParams);
        });

        useEffect(() => {
            if (deleteLoadStatus === LoadStatus.Ok) {
                notifyInSnackbar({ title: 'Partition Removed' });
            }
        }, [deleteLoadStatus, notifyInSnackbar]);

        const navigate = useNavigate();

        useEffect(() => {
            if (queryParams) {
                navigate(
                    { search: partitionsQueryParamsHandler.toString(queryParams) },
                    { replace: true },
                );
            }
        }, [queryParams, navigate]);

        return (
            <Stack direction="column" spacing={2}>
                <Text variant="headline" el="h3">
                    Partitions
                </Text>
                <DebouncedSearch
                    externalValue={searchFilter}
                    onChange={filterBySearch}
                    placeholder="Search by Name or ID"
                />
                <Table
                    sortable
                    striped={false}
                    tableState={tableState}
                    loading={
                        deleteLoadStatus === LoadStatus.Loading ||
                        partitionsLoadStatus === LoadStatus.Loading
                    }
                >
                    <TableColumn field="displayName" title="Name" />
                    <TableColumn field="externalId" title="ID" />
                    <TableColumn
                        locked
                        field=""
                        width={138}
                        cell={ActionsCell}
                        headerCell={() => null}
                    />
                </Table>
            </Stack>
        );
    }),
);
